import { HotelParams, SCENARIOS, ScenarioKey, SCENARIO_FACTORS } from '@/models/HotelParams';
import { Calculator, CalcResult } from '@/services/Calculator';
import { StorageService } from '@/services/StorageService';
import { validateParams } from '@/utils/validation';
import { formatEuro, formatHours, formatMonths, formatNumber, formatPercent } from '@/utils/format';
import { el, icon, tooltip, statCard, renderSymbol } from '@/utils/dom';
import { Chart, registerables } from 'chart.js';
import { HourlyCostModal } from '@/components/HourlyCostModal';
import { debounceTime, distinctUntilChanged, fromEvent } from 'rxjs';

Chart.register(...registerables);

interface FieldDef {
  key: keyof HotelParams;
  label: string;
  symbol: string; // simbolo matematico (N, O, ΔO, ...)
  unit?: string;
  min: number;
  max: number;
  step: number;
  kind: 'number' | 'slider' | 'percent-slider';
  tip: string;
  group: 'hotel' | 'costs' | 'uplift';
  /** Se true, il campo mostra un pulsante "calcola" che apre la modale C_h. */
  hasCalcModal?: boolean;
}

const FIELDS: FieldDef[] = [
  { key: 'employeesCount', label: 'Numero dipendenti', symbol: 'E', unit: '', min: 0, max: 1000, step: 1, kind: 'number', tip: 'Numero totale di dipendenti dell’hotel.', group: 'hotel' },
  { key: 'rooms', label: 'Numero stanze', symbol: 'N', unit: '', min: 1, max: 1000, step: 1, kind: 'number', tip: 'Totale delle camere dell’hotel, indipendentemente dall’occupazione.', group: 'hotel' },
  { key: 'daysPerYear', label: 'Giorni / anno', symbol: 'D', unit: 'gg', min: 1, max: 366, step: 1, kind: 'number', tip: 'Giorni di apertura nell’anno. Per hotel sempre aperti: 365.', group: 'hotel' },
  { key: 'occupancy', label: 'Occupazione media', symbol: 'O', unit: '%', min: 0, max: 1, step: 0.01, kind: 'percent-slider', tip: 'Percentuale media di stanze vendute nell’anno. 65% = 0,65.', group: 'hotel' },
  { key: 'manualMinutesPerRoom', label: 'Minuti manuali x prenotazione', symbol: 'T_manual', unit: 'min', min: 0, max: 240, step: 1, kind: 'slider', tip: 'Tempo stimato di lavoro manuale (inserimento prenotazioni, check-in, sync OTA, fatturazione) per ogni stanza occupata, ogni giorno.', group: 'hotel' },
  { key: 'manualMinutesAdministrative', label: 'Minuti manuali x amministrazione', symbol: 'T_admin', unit: 'min', min: 60, max: 320, step: 1, kind: 'slider', tip: 'Tempo stimato di lavoro amministrativo (gestione documenti, reporting, fatturazione, etc.) per ogni giorno.', group: 'hotel' },
  { key: 'otaPercentage', label: '% Prenotazioni OTA', symbol: 'OTA%', unit: '%', min: 0, max: 1, step: 0.01, kind: 'percent-slider', tip: 'Percentuale di prenotazioni dovute agli OTA.', group: 'hotel' },
  { key: 'otaCommission', label: 'Commissione OTA', symbol: 'OTA_comm', unit: '%', min: 0, max: 0.3, step: 0.01, kind: 'percent-slider', tip: 'Percentuale di commissioni pagate agli OTA.', group: 'hotel' },
  { key: 'hourlyCost', label: 'Costo orario', symbol: 'C_h', unit: '€/h', min: 5, max: 60, step: 0.5, kind: 'number', tip: 'Costo orario del lavoro (stipendio lordo + oneri). In Italia tipicamente 15-22 €/h per addetto reception.', group: 'costs', hasCalcModal: true },
  { key: 'adr', label: 'Tariffa media per notte', symbol: 'ADR', unit: '€', min: 20, max: 1000, step: 1, kind: 'number', tip: 'Average Daily Rate: prezzo medio di vendita per camera occupata.', group: 'costs' },
  { key: 'deltaOccupancy', label: 'Incremento occupazione', symbol: 'ΔO', unit: '%', min: 0, max: 0.3, step: 0.005, kind: 'percent-slider', tip: 'Incremento (sul totale) atteso di occupazione grazie al sistema integrato (channel manager + booking engine). Es. Occupazione = 65%, Incremento =  3-8%, Nuova occupazione = 68-73%.', group: 'uplift' },
  // { key: 'systemAnnualCost', label: 'Costo annuo sistema', symbol: 'C_cybhotel', unit: '€/anno', min: 0, max: 200000, step: 100, kind: 'number', tip: 'Costo annuo ricorrente del sistema integrato (canone PMS + booking engine + channel manager + pagamenti + totem). Esclude CapEx hardware iniziale.', group: 'uplift' },
  { key: 'upsellingIncrease', label: 'Incremento Upselling', symbol: '%Upselling', unit: '%', min: 0, max: 0.3, step: 0.001, kind: 'percent-slider', tip: 'Incremento atteso dell’upselling grazie al sistema integrato (servizi, extra , F&B). Tipico 3-8%.', group: 'uplift' },
  { key: 'actualSystemCost', label: 'Costo sistema attuale', symbol: 'C_sistema', unit: '€/anno', min: 0, max: 200000, step: 100, kind: 'number', tip: 'Costo annuo ricorrente del sistema integrato attualmente in uso (canone PMS + booking engine + channel manager + pagamenti + totem). Esclude CapEx hardware iniziale.', group: 'costs' },
  { key: 'directOccupancyIncrease', label: 'Incremento prenotazioni dirette', symbol: 'ΔO_direct', unit: '%', min: 0, max: 0.3, step: 0.005, kind: 'percent-slider', tip: 'Incremento (sul totale) atteso di occupazione diretta grazie al sistema integrato (channel manager + booking engine). Es. Prenotazioni OTA Attuali = 75%, Incremento Diretto = 10%, Nuove Prenotazioni Dirette = 35%. Nuove Prenotazioni OTA = 65%.', group: 'uplift' },
];

const GROUP_LABELS: Record<FieldDef['group'], string> = {
  hotel: 'Dati hotel',
  costs: 'Costi & ricavi',
  uplift: 'Effetti del sistema Cybhotel Basati su 10 anni di esperienza con i nostri clienti',
};

const SCENARIO_LABELS: Record<ScenarioKey, string> = {
  conservative: 'Conservativo',
  realistic: 'Realistico',
  aggressive: 'Aggressivo',
};

const SCENARIO_COLORS: Record<ScenarioKey, string> = {
  conservative: '#94a3b8',
  realistic: '#14b8a6',
  aggressive: '#6366f1',
};

export class CalculatorView {
  private params: HotelParams;
  private calculator: Calculator;
  private chart?: Chart;
  private onParamsChange?: (p: HotelParams) => void;
  private detailsOpen = false;
  private legendOpen = false;

  constructor(opts: { params: HotelParams; onParamsChange?: (p: HotelParams) => void } = { params: StorageService.load() }) {
    this.params = opts.params;
    this.calculator = new Calculator(this.params);
    this.onParamsChange = opts.onParamsChange;
  }

  mount(root: HTMLElement): void {
    root.innerHTML = '';
    root.setAttribute('aria-label', 'Calcolatore ROI');

    // HERO
    root.appendChild(
      el('section', { className: 'mb-6 animate-fade-in' }, [
        el('div', { className: 'inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-medium mb-3' }, [
          el('span', { className: 'w-1.5 h-1.5 rounded-full bg-brand-500' }),
          el('span', { text: 'PMS · Booking · Channel Manager · Check-in · Pagamenti · Totem' }),
        ]),
        el('h1', { className: 'text-3xl sm:text-4xl font-bold text-slate-900' }, [
          // document.createTextNode(' '),
          el('span', { className: 'text-slate-800', text: 'Calcolatore del risparmio' })
          // document.createTextNode(' e '),
          // el('span', { className: 'text-brand-600', text: 'ROI' }),
        ]),
        el('h2', { className: 'mt-2 text-sm text-2xl sm:text-3xl text-slate-500 max-w-2xl', text: ' per strutture ricettive' }),
        el('p', { className: 'mt-3 max-w-2xl text-accent-600', text: 'Stai lasciando soldi sul tavolo? Calcola quanto potresti risparmiare con il nostro sistema integrato.' }),
        el('p', { className: 'mt-3 text-slate-600 max-w-2xl', text: 'Inserisci i parametri del tuo hotel e ottieni subito una stima di risparmio netto, valore aggiunto e payback period. Confronta tre scenari: conservativo, realistico, aggressivo.' }),
      ]),
    );

    // LEGENDA VARIABILI (popover)
    root.appendChild(this.renderVariablesLegend());

    // Quick scenarios
    root.appendChild(this.quickScenariosRow());

    // Form + Risultati (grid 2 colonne su desktop)
    const grid = el('div', { className: 'grid gap-6 lg:grid-cols-5' });
    grid.appendChild(this.renderFormColumn());
    grid.appendChild(this.renderResultsColumn());
    root.appendChild(grid);


    const grid2 = el('div', { className: 'grid gap-3 lg:grid-cols-3 md:grid-cols-1 mt-6 comparison' });

    // Scenari comparativi (full width sotto)
    grid2.appendChild(this.renderScenarioComparison());
    grid2.appendChild(this.renderChartCard());

    root.appendChild(grid2);

    // Dettagli formule (collassabile)
    root.appendChild(this.renderFormulaDetails());
  }

  /** Popover con la legenda delle variabili principali (N, O, T_manual, ...). */
  private renderVariablesLegend(): HTMLElement {
    const items: { sym: string; desc: string }[] = [
      { sym: 'N', desc: 'numero di stanze' },
      { sym: 'O', desc: 'occupazione media (0–1)' },
      { sym: 'T_manual', desc: 'ore di inserimento dati per stanza occupata al giorno' },
      { sym: 'D', desc: 'giorni operativi/anno' },
      { sym: 'C_h', desc: 'costo orario del lavoro (€)' },
      { sym: 'C_cybhotel', desc: 'costo annuo totale del sistema cybhotel (€)' },
      { sym: 'C_sistema', desc: 'costo annuo totale del sistema attuale (€)' },
      { sym: 'C_commissioni-OTA', desc: 'Costo annuo delle commissioni OTA(€)' },
      { sym: 'ADR', desc: 'Average Daily Rate — prezzo medio camera venduta (€)' },
      { sym: 'ΔO', desc: 'incremento atteso di occupazione (0–1)' },
      { sym: 'Upselling', desc: 'incremento atteso upselling (%)' },
      { sym: 'ΔO_direct', desc: 'incremento atteso di occupazione diretto (0–1)' },
      { sym: 'OTA%', desc: 'percentuale di prenotazioni dovute agli OTA (0–1)' },
      { sym: 'OTA_comm', desc: 'percentuale di commissioni pagate agli OTA (0–1)' },
      { sym: 'O_new', desc: 'nuova occupazione (O + ΔO)' },
    ];

    const details = el('details', { className: 'card p-0 overflow-hidden mb-6 group' });
    if (this.legendOpen) details.setAttribute('open', 'open');
    details.addEventListener('toggle', () => {
      this.legendOpen = details.hasAttribute('open');
    });

    const summary = el('summary', {
      className: 'cursor-pointer list-none flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition font-medium text-slate-800',
    }, [
      el('div', { className: 'flex items-center gap-2' }, [
        el('span', { className: 'w-1.5 h-5 rounded bg-accent-500' }),
        el('span', { text: 'Legenda variabili principali' }),
      ]),
      el('span', { className: 'text-accent-600 transition-transform group-open:rotate-180', html: '<i class="fa-solid fa-chevron-down"></i>' }),
    ]);

    const body = el('div', { className: 'px-5 pb-5' }, [
      el('p', { className: 'text-sm text-slate-600 mb-3', text: 'Questi sono i simboli usati nelle formule di calcolo. Trovi ogni parametro nel form qui sotto con la stessa etichetta.' }),
      el('dl', { className: 'grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2' }, items.map((it) =>
        el('div', { className: 'flex items-baseline gap-2 text-sm' }, [
          el('dt', {
            className: 'font-mono font-semibold text-brand-700 min-w-[5rem]',
            ...renderSymbol(it.sym)
          }),
          el('dd', { className: 'text-slate-600', text: it.desc }),
        ]),
      )),
    ]);

    details.appendChild(summary);
    details.appendChild(body);
    return details;
  }

  /** Row di scenari rapidi (small/medium/large). */
  private quickScenariosRow(): HTMLElement {
    const buttons = (Object.keys(SCENARIOS) as Array<keyof typeof SCENARIOS>).map((k) => {
      const s = SCENARIOS[k];
      return el('button', {
        className: 'btn-secondary !py-1.5 text-xs',
        onClick: () => this.updateParams(s.params, true),
      }, [el('span', { className: 'font-semibold', text: String(s.params.rooms) }), el('span', { text: ' stanze' })]);
    });
    return el('section', { className: 'mb-6 flex flex-wrap items-center gap-2' }, [
      el('span', { className: 'text-xs font-medium text-slate-500', text: 'Carica scenario:' }),
      ...buttons,
    ]);
  }

  // ----------------------- FORM -----------------------

  private renderFormColumn(): HTMLElement {
    const col = el('section', { className: 'lg:col-span-3 space-y-6', aria: { label: 'Form parametri' } });

    const groups: Record<FieldDef['group'], FieldDef[]> = { hotel: [], costs: [], uplift: [] };
    FIELDS.forEach((f) => groups[f.group].push(f));

    (Object.keys(groups) as FieldDef['group'][]).forEach((g) => {
      const card = el('div', { className: 'card' }, [
        el('h2', { className: 'text-base! font-semibold text-slate-900! mb-4! flex items-center gap-2' }, [
          el('span', { className: 'w-1.5 h-5 rounded bg-brand-500' }),
          el('span', { text: GROUP_LABELS[g] }),
        ]),
        el('div', { className: 'grid sm:grid-cols-2 gap-4' }, groups[g].map((f) => this.renderField(f))),
      ]);
      col.appendChild(card);
    });

    const card = el('div', { className: 'card' }, [
      el('h2', { className: 'text-base! font-semibold! text-slate-900 mb-4! flex items-center! gap-2!' }, [
        el('span', { className: 'w-1.5 h-5 rounded bg-brand-500' }),
        el('span', { text: 'Vuoi risparmiare ancora di più?' }),
      ]),
      el('p', { className: 'text-sm text-slate-600', text: 'Contatta il nostro team per informazioni sui piani di risparmio personalizzati.' }),
      el('a', {
        className: 'btn-primary mt-3',
        attrs: { href: 'mailto:info@cybhotel.com' },
        text: 'Contatta il nostro team'
      })
    ]);
    col.appendChild(card);
    return col;
  }

  private renderField(def: FieldDef): HTMLElement {
    const errors = validateParams(this.params);
    const error = errors[def.key];
    const value = this.params[def.key] ?? 0;

    console.info('Rendering field', def.key, 'with value', value, 'and error', error, this.params[def.key]);


    const wrapper = el('div', { className: 'flex flex-col gap-1' });

    // Label row (con simbolo + tooltip)
    const label = el('label', { className: 'field-label flex items-center gap-1.5', attrs: { for: `field-${def.key}` } }, [
      el('span', { text: def.label }),
      el('span', {
        className: 'font-mono! text-xs! text-slate-400!',
        ...renderSymbol(def.symbol, '(', ')'),
      }),
      tooltip(def.tip),
    ]);

    wrapper.appendChild(label);

    if (def.kind === 'number') {
      // Numeric stepper: input number + bottoni + (opz.) modale C_h
      const input = el('input', {
        className: 'input-base! text-center! tabular-nums!',
        attrs: {
          id: `field-${def.key}`,
          type: 'number',
          inputmode: 'decimal',
          min: String(def.min),
          max: String(def.max),
          step: String(def.step),
          value: String(value),
          'aria-label': def.label,
        }
      })

      fromEvent(input, 'input')
        .pipe(
          debounceTime(600),
          distinctUntilChanged()
        )
        .subscribe(e => {
          const t = e.target as HTMLInputElement;
          if (t.value === '' || t.value === '-' || t.value.endsWith('.')) return;
          const n = Number(t.value);
          if (Number.isFinite(n)) this.updateSingle(def.key, n);
        })


      const stepper = el('div', { className: 'flex items-stretch gap-1' }, [
        el('button', {
          className: 'btn-secondary px-2.5! py-1! text-base!',
          aria: { label: `Diminuisci ${def.label}` },
          attrs: { type: 'button' },
          onClick: () => {
            const next = Math.max(def.min, +(value - def.step).toFixed(4));
            this.updateSingle(def.key, next);
          },
        }, [el('span', { text: '−' })]),
        input,
        el('button', {
          className: 'btn-secondary !px-2.5 !py-1 text-base',
          aria: { label: `Aumenta ${def.label}` },
          attrs: { type: 'button' },
          onClick: () => {
            const next = Math.min(def.max, +(value + def.step).toFixed(4));
            this.updateSingle(def.key, next);
          },
        }, [el('span', { text: '+' })]),
        ...(def.hasCalcModal ? [
          el('button', {
            className: 'btn-secondary !px-2.5 !py-1 text-xs',
            attrs: { type: 'button', title: 'Calcola da stipendio mensile' },
            aria: { label: `Calcola ${def.label} da stipendio mensile` },
            onClick: () => this.openHourlyCostModal(),
          }, [icon('calculator'), el('span', { className: 'hidden sm:inline', text: 'Calc' })]),
        ] : []),
      ]);
      wrapper.appendChild(stepper);
    } else {
      // SLIDER + INPUT EDITABILE SULLA STESSA RIGA
      // La label diventa un input editabile: l'utente può trascinare lo slider
      // OPPURE digitare il valore direttamente.
      const isPercent = def.kind === 'percent-slider';
      const inputMin = def.min;
      const inputMax = def.max;
      const inputStep = def.step;

      // Valore iniziale mostrato nell'input (per percentuali: 0-100, per gli altri: valore raw)
      const inputInitial = isPercent ? (value * 100).toFixed(1) : String(value);

      const slider = el('input', {
        attrs: {
          id: `field-${def.key}`,
          type: 'range',
          min: String(def.min),
          max: String(def.max),
          step: String(def.step),
          value: String(value),
          'aria-label': def.label,
          'aria-valuetext': isPercent ? formatPercent(value, 1) : `${value} ${def.unit || ''}`.trim(),
        },
        onInput: (e) => {
          const v = Number((e.target as HTMLInputElement).value);
          // aggiorna anche l'input numerico
          const numInput = wrapper.querySelector<HTMLInputElement>('.slider-number-input');
          if (numInput) numInput.value = isPercent ? (v * 100).toFixed(1) : String(v);
          this.updateSingle(def.key, v, /* skipRerender */ true);
        },
        onChange: (e) => {
          const v = Number((e.target as HTMLInputElement).value);
          this.updateSingle(def.key, v);
        },
      });

      // Input numerico editabile: è la "label" che diventa input
      const numberInput = el('input', {
        className: 'input-base slider-number-input !w-20 !py-1 text-center text-sm font-semibold text-brand-700 tabular-nums',
        attrs: {
          type: 'number',
          inputmode: 'decimal',
          min: String(isPercent ? inputMin * 100 : inputMin),
          max: String(isPercent ? inputMax * 100 : inputMax),
          step: String(isPercent ? inputStep * 100 : inputStep),
          value: inputInitial,
          'aria-label': `${def.label} (valore numerico)`,
        },
        onChange: (e) => {
          const t = e.target as HTMLInputElement;
          const raw = Number(t.value);
          if (!Number.isFinite(raw)) return;
          const v = isPercent ? raw / 100 : raw;
          const clamped = Math.min(Math.max(v, def.min), def.max);
          // sync slider
          const sliderEl = wrapper.querySelector<HTMLInputElement>(`#field-${def.key}`);
          if (sliderEl) sliderEl.value = String(clamped);
          this.updateSingle(def.key, clamped);
        },
        onInput: (e) => {
          const t = e.target as HTMLInputElement;
          if (t.value === '' || t.value === '-' || t.value.endsWith('.')) return;
          const raw = Number(t.value);
          if (!Number.isFinite(raw)) return;
          const v = isPercent ? raw / 100 : raw;
          const clamped = Math.min(Math.max(v, def.min), def.max);
          const sliderEl = wrapper.querySelector<HTMLInputElement>(`#field-${def.key}`);
          if (sliderEl) sliderEl.value = String(clamped);
          this.updateSingle(def.key, clamped, /* skipRerender */ true);
        },
      });

      // Unit suffix
      const suffix = el('span', { className: 'text-xs text-slate-500 font-medium w-6 text-left', text: def.unit || '' });

      // Tutto sulla stessa riga: slider flessibile + input + unit
      wrapper.appendChild(el('div', { className: 'flex items-center gap-2' }, [
        slider,
        numberInput,
        suffix,
      ]));
    }

    if (error) {
      wrapper.appendChild(el('p', { className: 'text-xs text-rose-600', text: error }));
    }

    return wrapper;
  }

  // ----------------------- MODALE C_h ----------------------
  private openHourlyCostModal(): void {
    const modal = new HourlyCostModal({
      onApply: (hourlyCost: number) => {
        this.updateSingle('hourlyCost', hourlyCost);
      },
      currentMonthly: this.params.hourlyCost * 8 * 26, // stima iniziale
    });
    modal.open();
  }

  // ----------------------- RISULTATI -----------------------
  private renderResultsColumn(): HTMLElement {
    const col = el('section', { className: 'lg:col-span-2 space-y-4', aria: { label: 'Risultati' } });
    const r = this.calculator.compute();

    // Hero risultato
    col.appendChild(
      el('div', { className: 'card bg-gradient-to-br from-slate-900 to-slate-800 text-white border-slate-800' }, [
        el('span', { className: 'text-xs uppercase tracking-wide text-slate-300', text: 'Risparmio netto annuo' }),
        el('p', { className: 'mt-1 !text-3xl !sm:text-4xl font-bold tabular-nums', text: formatEuro(r.total.netSavings) }),
        el('div', { className: 'mt-4 pt-4 border-t border-slate-700 flex items-center justify-between' }, [
          el('div', {}, [
            el('span', { className: 'text-xs text-slate-300', text: 'Payback period' }),
            el('p', { className: 'text-lg font-semibold', text: formatMonths(r.total.paybackMonths) }),
          ]),
          el('div', { className: 'text-right' }, [
            el('span', { className: 'text-xs text-slate-300', text: 'ROI / mese' }),
            el('p', { className: 'text-lg font-semibold', text: Number.isFinite(r.total.paybackMonths) ? `${(100 / r.total.paybackMonths).toFixed(1)}%` : '—' }),
          ]),
        ]),
      ]),
    );


    const newOtaPecent = this.params.otaPercentage - this.params.directOccupancyIncrease
    const directPecent = 1 - (this.params.otaPercentage - this.params.directOccupancyIncrease) 
    // Stat cards breakdown
    col.appendChild(
      el('div', { className: 'grid grid-cols-2 gap-3' }, [

        // RICAVI
        statCard('Fatturato Lordo (Attuale)', formatEuro(r.actual.revenue), { hint: `ricavo lordo attuale`, tone: 'default' }),
        statCard('Fatturato Lordo (Potenziale)', formatEuro(r.pms.revenue), { hint: `possibile fatturato con cybhotel`, tone: 'positive' }),
        // ORE
        statCard('Costo Attuale Dipendenti', formatEuro(r.actual.employeesCost), { hint: `ore stimate attuali: ${formatHours(r.actual.employeesHours)}`, tone: 'positive' }),
        statCard('Costo Orario risparmiato', formatEuro(r.savings.savingsHoursCost), { hint: 'N · O_new · T_manual · D · C_h', tone: 'positive' }),
        statCard('Ore Risparmiate', formatHours(r.savings.savingHours), { hint: 'N · O_new · T_manual · D, ore risparmiate in un anno', tone: 'positive' }),
        statCard('Dipendenti non più necessari', formatNumber(r.savings.unnecessaryEmployee), { hint: `basato sulla stima (OCSA) di ${this.calculator.totalWorkHoursPerEmployee().toFixed(0)}(h/anno) per dipendente`, tone: 'positive' }),

        // OTA
        statCard('Commissione OTA Attuale', formatEuro(r.actual.otaCommissionCost), { hint: 'OTA% * OTA_Comm * ADR * N * D * O', tone: 'default' }),
        statCard('Commissione OTA Risparmiata', formatEuro(r.savings.commissions), { hint: `nuovo OTA%: ${formatPercent(newOtaPecent)} nuovo Dirette%: ${formatPercent(directPecent)} (OTA% - ΔO_direct) * OTA_Comm * ADR * N * D * O`, tone: 'positive' }),

        statCard('Incremento Ricavato per aumento Occupazione', formatEuro(r.savings.deltaRevenue), { hint: 'N · ΔO · ADR · D', tone: 'positive' }),
        statCard('Incremento Ricavato per Upselling', formatEuro(r.savings.upsellingIncrease), { hint: 'incremento nel fatturato grazie all\'upselling', tone: 'positive' }),

        // COSTI
        statCard('Costi Attuali', formatEuro(r.actual.totalCost), { hint: `C_dipendenti (${formatEuro(r.actual.employeesCost)})  + C_commissioni-OTA (${formatEuro(r.actual.otaCommissionCost)}) + C_sistema (${formatEuro(r.actual.systemCost)})`, tone: 'negative' }),
        statCard('Costi Con Cybhotel', formatEuro(r.pms.totalCost), { hint: `C_dipendenti (${formatEuro(r.pms.employeesCost )})  + C_commissioni-OTA (${formatEuro(r.pms.otaCommissionCost)}) + C_cybhotel (${formatEuro(r.pms.systemCost)})`, tone: 'negative' }),
      ]),
    );


    return col;
  }

  private renderChartCard(): HTMLElement {
    const card = el('div', { className: 'card' }, [
      el('div', { className: 'flex items-center justify-between mb-3' }, [
        el('h3', { className: 'text-base font-semibold text-slate-900', text: 'Confronto scenari' }),
        el('span', { className: 'text-xs text-slate-500', text: 'Risparmio netto (€/anno)' }),
      ]),
      el('div', { className: 'relative h-80' }, [el('canvas', { attrs: { id: 'scenario-chart', role: 'img', 'aria-label': 'Grafico confronto scenari: risparmio netto per scenario conservativo, realistico, aggressivo.' } })]),
    ]);
    // Chart rendering differito (canvas deve essere in DOM)
    queueMicrotask(() => this.renderChart());
    return card;
  }

  private renderChart(): void {
    const canvas = document.getElementById('scenario-chart') as HTMLCanvasElement | null;
    if (!canvas) return;
    this.chart?.destroy();

    const keys = Object.keys(SCENARIO_FACTORS) as ScenarioKey[];
    const data = keys.map((k) => this.calculator.computeScenario(k).total.netSavings);

    this.chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: keys.map((k) => SCENARIO_LABELS[k]),
        datasets: [
          {
            label: 'Risparmio netto (€/anno)',
            data,
            backgroundColor: keys.map((k) => SCENARIO_COLORS[k]),
            borderRadius: 8,
            maxBarThickness: 64,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${formatEuro(Number(ctx.raw))}`,
            },
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: '#475569', font: { size: 12 } } },
          y: {
            grid: { color: '#f1f5f9' },
            ticks: {
              color: '#475569',
              font: { size: 11 },
              callback: (v) => formatEuro(Number(v)),
            },
          },
        },
      },
    });
  }

  // ----------------------- SCENARIO COMPARISON TABLE -----------------------
  private renderScenarioComparison(): HTMLElement {
    const keys = Object.keys(SCENARIO_FACTORS) as ScenarioKey[];
    const rows = keys.map((k) => {
      const r = this.calculator.computeScenario(k);
      return { k, r };
    });

    const headerCells = [
      el('th', { className: 'text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wide', text: 'Metrica' }),
      ...keys.map((k) =>
        el('th', { className: 'text-right py-3 px-4 text-xs font-medium uppercase tracking-wide', attrs: { style: `color:${SCENARIO_COLORS[k]}` }, text: SCENARIO_LABELS[k] }),
      ),
    ];

    const metricRow = (label: string, getter: (r: CalcResult) => string) =>
      el('tr', { className: 'border-t border-slate-100' }, [
        el('td', { className: 'py-3 px-4 text-sm text-slate-700', text: label }),
        ...rows.map(({ r }) => el('td', { className: 'py-3 px-4 text-right text-sm font-semibold tabular-nums', text: getter(r) })),
      ]);

    const table = el('table', { className: 'w-full' }, [
      el('thead', {}, [el('tr', {}, headerCells)]),
      el('tbody', {}, [
        metricRow('Risparmio costo ore', (r) => formatEuro(r.savings.savingsHoursCost)),
        metricRow('Risparmio Commissioni OTA', (r) => formatEuro(r.savings.commissions)),
        metricRow('Ricavo da incremento occupazione', (r) => formatEuro(r.savings.deltaRevenue)),
        metricRow('Ricavo da upselling', (r) => formatEuro(r.savings.upsellingIncrease)),
        metricRow('Costo sistema', (r) => formatEuro(r.pms.systemCost)),
        metricRow('Risparmio netto', (r) => formatEuro(r.total.netSavings)),
        metricRow('Payback', (r) => formatMonths(r.total.paybackMonths)),
      ]),
    ]);

    return el('section', { className: 'card lg:col-span-2 md:col-span-1' }, [
      el('div', { className: 'flex items-center justify-between mb-3' }, [
        el('h2', { className: 'text-base font-semibold text-slate-900', text: 'Scenario comparison' }),
        el('div', { className: 'flex items-center gap-2' }, keys.map((k) =>
          el('span', { className: 'inline-flex items-center gap-1.5 text-xs text-slate-600' }, [
            el('span', { className: 'w-2.5 h-2.5 rounded-sm', attrs: { style: `background:${SCENARIO_COLORS[k]}` } }),
            el('span', { text: SCENARIO_LABELS[k] }),
          ]),
        )),
      ]),
      el('div', { className: 'overflow-x-auto' }, [table]),
      el('p', { className: 'mt-3 text-xs text-slate-500', text: 'Lo scenario conservativo riduce i benefici attesi del 30-40%; quello aggressivo li incrementa del 30-50%. Il costo del sistema è uguale in tutti gli scenari.' }),
    ]);
  }

  // ----------------------- FORMULA DETAILS -----------------------
  private renderFormulaDetails(): HTMLElement {
    const details = el('details', { className: 'card mt-6 group' });
    if (this.detailsOpen) details.setAttribute('open', 'open');
    details.addEventListener('toggle', () => {
      this.detailsOpen = details.hasAttribute('open');
    });

    const summary = el('summary', {
      className: 'cursor-pointer list-none flex items-center justify-between gap-3 font-semibold text-slate-800',
    }, [
      el('span', { text: 'Mostra dettagli formule' }),
      el('span', { className: 'text-brand-600 transition-transform group-open:rotate-180', html: '<i class="fa-solid fa-chevron-down"></i>' }),
    ]);

    const body = el('div', { className: 'mt-4 space-y-5 text-sm text-slate-700' }, [
      el('div', { className: 'grid sm:grid-cols-2 gap-4' }, [
        this.formulaCard('Risparmio ore', 'N × O × T_manual × D × C_h', 'Stanze × occupazione × ore manuali risparmiate × giorni/anno × costo orario. Converte il tempo risparmiato in valore monetario.'),
        this.formulaCard('Valore occupazione', 'N × ΔO × ADR × D', 'Stanze × incremento occupazione × ADR × giorni. Stima i ricavi aggiuntivi per le camere vendute in più grazie al sistema.'),
        this.formulaCard('Upselling', '(N × D) × ADR × (Upselling)', 'Camere-disponibili × ADR × percentuale di incremento Upselling. Quantifica l’upselling di servizi extra.'),
        this.formulaCard('Risparmio netto', 'Risparmio ore + Valore occupazione + Valore TRevPAR − C_cybhotel', 'Somma dei benefici meno il costo annuo del sistema. È il flusso di cassa netto generato ogni anno.'),
        this.formulaCard('Payback mesi', '(C_cybhotel / Risparmio netto) × 12', 'Tempo necessario per recuperare il costo annuo del sistema tramite il risparmio netto. Se il risparmio netto è ≤ 0, il payback è infinito (mai rientro).'),
        this.formulaCard('Costo orario', 'C_h = C_m / 8 / 26', 'Ricava il costo orario dallo stipendio mensile: 8 ore/giorno lavorate, 26 giorni lavorativi/mese. Usa il pulsante "Calc" accanto a C_h per il calcolo automatico.'),
      ]),
      el('div', { className: 'rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600' }, [
        el('p', { className: 'font-medium text-slate-700 mb-1', text: 'Esempio numerico (scenario small)' }),
        el('p', { text: '20 stanze, 65% occupazione, 0.4 h/stanza, 365 giorni, 18 €/h, ADR 95 €, ΔO 5%, TRevPAR +4%, costo sistema 6.000 €/anno.' }),
        el('p', { className: 'mt-1', text: `Risparmio ore = 20 × 0.65 × 0.4 × 365 × 18 = ${formatEuro(20 * 0.65 * 0.4 * 365 * 18)}` }),
        el('p', { text: `Valore occupazione = 20 × 0.05 × 95 × 365 = ${formatEuro(20 * 0.05 * 95 * 365)}` }),
        el('p', { text: `Upselling = (20 × 365) × 95 × 0.04 = ${formatEuro(20 * 365 * 95 * 0.04)}` }),
      ]),
    ]);

    details.appendChild(summary);
    details.appendChild(body);
    return details;
  }

  private formulaCard(title: string, formula: string, explain: string): HTMLElement {
    return el('div', { className: 'rounded-lg border border-slate-200 p-4 bg-white' }, [
      el('p', { className: 'text-xs font-semibold text-slate-900 mb-1', text: title }),
      el('p', { className: 'font-mono text-sm text-brand-700 mb-2', ...renderSymbol(formula) }),
      el('p', { className: 'text-xs text-slate-600', ...renderSymbol(explain) }),
    ]);
  }

  // ----------------------- AGGIORNAMENTI -----------------------

  private updateSingle(key: keyof HotelParams, value: number, skipRerender = false): void {
    this.params = { ...this.params, [key]: value };
    this.calculator = new Calculator(this.params);
    StorageService.save(this.params);
    this.onParamsChange?.(this.params);
    if (!skipRerender) {
      this.rerender();
    } else {
      // Aggiorna solo i risultati + chart senza ricostruire tutto il form
      // (mantiene il focus sull'input attivo)
      this.refreshResultsOnly();
    }
  }

  private updateParams(p: HotelParams, persist: boolean): void {
    this.params = { ...p };
    this.calculator = new Calculator(this.params);
    if (persist) StorageService.save(this.params);
    this.onParamsChange?.(this.params);
    this.rerender();
  }

  /** Aggiorna solo risultati + chart (live update su slider drag). */
  private refreshResultsOnly(): void {
    const r = this.calculator.compute();
    // Sostituisce la colonna risultati se esiste
    const resultsCol = document.querySelector('.lg\\:col-span-2.space-y-4');
    if (resultsCol && resultsCol.parentNode) {
      const newCol = this.renderResultsColumn();
      resultsCol.parentNode.replaceChild(newCol, resultsCol);
    }
    // Aggiorna la tabella scenari
    const scenarioSection = document.querySelectorAll('.comparison')[0] as HTMLElement | undefined;
    if (scenarioSection && scenarioSection.parentNode) {
      const newSec = this.renderScenarioComparison();
      const newChart = this.renderChartCard();
      const newSecWrapper = el('div', { className: 'grid gap-3 lg:grid-cols-3 md:grid-cols-1 mt-6 comparison' }, [newSec, newChart]);
      scenarioSection.parentNode.replaceChild(newSecWrapper, scenarioSection);
    }
    // Annuncio live
    const live = document.getElementById('aria-live');
    if (live) {
      live.textContent = `Aggiornato: risparmio netto ${formatEuro(r.total.netSavings)}, payback ${formatMonths(r.total.paybackMonths)}.`;
    }
  }

  private rerender(): void {
    const root = document.getElementById('app-root');
    if (root) {
      this.mount(root);
      const live = document.getElementById('aria-live');
      if (live) {
        const r = this.calculator.compute();
        live.textContent = `Aggiornato: risparmio netto ${formatEuro(r.total.netSavings)}, payback ${formatMonths(r.total.paybackMonths)}.`;
      }
    }
  }
}
